import {
  CategoryInfo,
  FinancialItems,
  financialAssetOptions,
} from 'src/financial/financial-data/hooks/useFinancialItems';
import {
  getFinancialItemEntityType,
  getFinancialItemType,
} from 'src/financial/financial-data/financialItemEntityType';
import {
  evenShare,
  hasUnrecordedCoOwner,
  ownerShares,
  type OwnerShare,
  shareOfValue,
  UNRECORDED_CO_OWNER,
} from 'src/financial/financial-data/ownerShares';

export type FinancialRow = {
  id: string;
  /**
   * The client whose list returned the item, which is the client it opens through: an owner with
   * the owners feature on, the holder with it off, and so always one the item page will accept.
   */
  openAsClientId: string;
  memberName: string;
  /** Every owner's name, for the family view, where no single member is "me". */
  ownerNames?: string[];
  /**
   * The viewing client's share of the item, for the individual view. Undefined in the family
   * view, where no single member is "me" and the owners are named instead.
   */
  ownerShare?: OwnerShare;
  product: string;
  navProduct: string;
  description?: string;
  provider?: string;
  typeLabel?: string;
  policyNumber?: string | null;
  balanceDate?: string;
  /** The client's share of the item, which is its whole value unless `fullBalance` is set. */
  balance: number | null;
  /** The item's whole value, present only on a joint item where `balance` is a share of it. */
  fullBalance?: number;
  /** `balance` is an equal split standing in for shares nobody recorded. */
  isShareAssumed?: boolean;
  isUpdatedAutomatically?: boolean;
  canConnectToFeed?: boolean;
};

/** The client whose list the items came from. */
type ListedFor = { clientId: string; name: string };

/**
 * Whose share of each item the rows report: one client's on their own Financial tab, or the
 * household's on the family view, where an item partly owned outside the family counts only
 * for the part its members own.
 */
type ShareContext =
  | { kind: 'client'; clientId: string }
  | { kind: 'household'; memberClientIds: string[] };

type FinancialItemWithOwners = {
  isJoint?: boolean | null;
  owners?: {
    clientId?: string | null;
    externalName?: string | null;
    ownershipPercentage?: number | null;
  }[];
};

/**
 * Resolves each recorded owner to a display name: a family member through the roster, an
 * external owner through its own name. Owners neither can name, clients outside the family, are
 * counted as "+N others" rather than dropped, since the family's share already leaves them out.
 * A joint item that records only its holder says its co-owner is missing, as the item page does,
 * so a holder's typed share is not read as the whole of who holds it.
 */
function ownerNames(
  item: FinancialItemWithOwners,
  resolveOwnerName: ((clientId: string) => string | undefined) | undefined,
): string[] | undefined {
  if (!resolveOwnerName) return undefined;
  const resolved = (item.owners ?? []).map(owner =>
    owner.clientId ? resolveOwnerName(owner.clientId) : owner.externalName,
  );
  const names = resolved.filter((name): name is string => !!name);
  if (names.length === 0) return undefined;
  const unnamed = resolved.length - names.length;
  const named =
    unnamed > 0 ? [...names, `+${unnamed} ${unnamed === 1 ? 'other' : 'others'}`] : names;
  return hasUnrecordedCoOwner(!!item.isJoint, resolved.length)
    ? [...named, UNRECORDED_CO_OWNER]
    : named;
}

const recordedShares = (item: FinancialItemWithOwners) =>
  (item.owners ?? []).map(owner => owner.ownershipPercentage);

/**
 * One client's share of an item, by the rule `ownerShares` sets out. Pensions, crypto and
 * collectibles record no `isJoint` at all, so they always read as wholly the client's. A client
 * the owners don't name, as on a legacy item read with the owners feature off, gets the even
 * split.
 */
function ownerShare(item: FinancialItemWithOwners, clientId: string): OwnerShare {
  if (!item.isJoint) return { percentage: 100, isAssumed: false };
  const owners = item.owners ?? [];
  const index = owners.findIndex(owner => owner.clientId === clientId);
  if (index < 0) return evenShare(owners.length);
  return ownerShares(recordedShares(item), true)[index];
}

/**
 * The family's share of an item: what its members own of it between them, which is less than
 * the whole only where someone outside the family is recorded as an owner.
 *
 * A joint item whose co-owner is not recorded counts the holder's share where the adviser typed
 * one: choosing "co-owner not recorded" and giving the holder a share says someone else holds the
 * rest. With no share typed it counts whole, as it did before owners were read. That is the
 * legacy backfill, and halving it would make a couple's home recorded once look half owned by an
 * outsider; that the per-member copies of a legacy joint item count twice instead is left for
 * advisers to resolve, not settled by a guess in the totals.
 */
function householdShare(item: FinancialItemWithOwners, memberClientIds: string[]): OwnerShare {
  const owners = item.owners ?? [];
  if (!item.isJoint) return { percentage: 100, isAssumed: false };
  if (hasUnrecordedCoOwner(true, owners.length) && owners[0]?.ownershipPercentage == null) {
    return { percentage: 100, isAssumed: false };
  }
  const memberShares = ownerShares(recordedShares(item), true).filter((_, index) => {
    const clientId = owners[index].clientId;
    return !!clientId && memberClientIds.includes(clientId);
  });
  // A listed item always has a member among its owners; should one ever arrive without, count it
  // whole like an unrecorded co-owner rather than as £0.
  if (memberShares.length === 0) return { percentage: 100, isAssumed: false };
  return {
    percentage: memberShares.reduce((sum, memberShare) => sum + memberShare.percentage, 0),
    isAssumed: memberShares.some(memberShare => memberShare.isAssumed),
  };
}

/** The share a row reports in its ownership column: only the individual view has a "me". */
function clientShare(
  item: FinancialItemWithOwners,
  share: ShareContext | undefined,
): OwnerShare | undefined {
  return share?.kind === 'client' ? ownerShare(item, share.clientId) : undefined;
}

/** The share of an item's value a row counts towards its totals. */
function countedShare(
  item: FinancialItemWithOwners,
  share: ShareContext | undefined,
): OwnerShare | undefined {
  if (!share) return undefined;
  if (share.kind === 'client') return ownerShare(item, share.clientId);
  return householdShare(item, share.memberClientIds);
}

/**
 * Narrows a value to the share counted, keeping the whole as context for the row. Totals and
 * net worth then add up the shares rather than counting a joint item in full on both members.
 */
function shareOfBalance(
  balance: number | null,
  share: OwnerShare | undefined,
): { balance: number | null; fullBalance?: number; isShareAssumed?: boolean } {
  if (!share || balance == null) return { balance };
  // Three members' thirds add up to a hair either side of 100 in floating point.
  if (Math.abs(share.percentage - 100) < 1e-9) return { balance };
  return {
    balance: shareOfValue(balance, share.percentage),
    fullBalance: balance,
    isShareAssumed: share.isAssumed,
  };
}

export type FinancialSubSection = {
  key: string;
  title: string;
  rows: FinancialRow[];
  total: number;
  /** The same total before shares were applied, present only where the two differ. */
  fullTotal?: number;
  showPolicyNumber?: boolean;
};

const POLICY_NUMBER_PRODUCTS = new Set(['InvestmentAndCash', 'Pension']);

type FinancialCategoryKey = 'spendingAccounts' | 'assets' | 'liabilities' | 'protections';

export type FinancialCategory = {
  key: FinancialCategoryKey;
  title: string;
  rows: FinancialRow[];
  total: number;
  /** The same total before shares were applied, present only where the two differ. */
  fullTotal?: number;
  totalLabel?: string;
  subSections?: FinancialSubSection[];
};

type GroupedRows = {
  spendingAccounts: FinancialRow[];
  assets: FinancialRow[];
  liabilities: FinancialRow[];
  protections: FinancialRow[];
};

export const emptyGroupedRows = (): GroupedRows => ({
  spendingAccounts: [],
  assets: [],
  liabilities: [],
  protections: [],
});

const sumBalances = (rows: FinancialRow[]) =>
  rows.reduce((acc, row) => acc + (row.balance ?? 0), 0);

/**
 * The shares a section adds up to, and what the same rows come to at their full values. A
 * section holding a joint item does not total the figures the adviser entered, so the full
 * total is carried alongside to say why; it is left off where the two agree.
 */
function totalsFor(rows: FinancialRow[]): { total: number; fullTotal?: number } {
  const total = sumBalances(rows);
  const fullTotal = rows.reduce((acc, row) => acc + (row.fullBalance ?? row.balance ?? 0), 0);
  return fullTotal === total ? { total } : { total, fullTotal };
}

export function extractRows(
  items: FinancialItems,
  listedFor: ListedFor,
  share?: ShareContext,
  resolveOwnerName?: (clientId: string) => string | undefined,
): GroupedRows {
  const { metadata } = items;

  return {
    spendingAccounts: items.spendingAccounts.map(item => ({
      id: item.id,
      openAsClientId: listedFor.clientId,
      memberName: listedFor.name,
      ownerNames: ownerNames(item, resolveOwnerName),
      ownerShare: clientShare(item, share),
      product: item.product,
      navProduct: item.product,
      description: item.description,
      provider: item.provider,
      typeLabel: getFinancialItemType(item, metadata),
      balanceDate: item.balanceDate,
      ...shareOfBalance(item.currentBalance, countedShare(item, share)),
    })),
    assets: items.assets.map(item => ({
      id: item.id,
      openAsClientId: listedFor.clientId,
      memberName: listedFor.name,
      ownerNames: ownerNames(item, resolveOwnerName),
      ownerShare: clientShare(item, share),
      product: item.product,
      navProduct: getFinancialItemEntityType(item),
      description: item.description,
      provider: item.provider,
      typeLabel: getFinancialItemType(item, metadata),
      policyNumber: 'policyNumber' in item ? item.policyNumber : undefined,
      balanceDate: item.valueDate,
      ...shareOfBalance(item.value, countedShare(item, share)),
      isUpdatedAutomatically: item.isUpdatedAutomatically,
      canConnectToFeed: item.canConnectToFeed,
    })),
    liabilities: items.liabilities.map(item => ({
      id: item.id,
      openAsClientId: listedFor.clientId,
      memberName: listedFor.name,
      ownerNames: ownerNames(item, resolveOwnerName),
      ownerShare: clientShare(item, share),
      product: item.product,
      navProduct: item.product,
      description: item.description,
      provider: item.provider,
      typeLabel: getFinancialItemType(item, metadata),
      balanceDate: item.remainingBalanceDate,
      ...shareOfBalance(item.remainingBalance, countedShare(item, share)),
    })),
    // A joint policy pays its cover in full to whoever claims, so a share of it would
    // understate what the client is actually protected for. The share is still shown.
    protections: items.protections.map(item => ({
      id: item.id,
      openAsClientId: listedFor.clientId,
      memberName: listedFor.name,
      ownerNames: ownerNames(item, resolveOwnerName),
      ownerShare: clientShare(item, share),
      product: item.product,
      navProduct: item.product,
      description: item.description,
      provider: item.provider,
      typeLabel: getFinancialItemType(item, metadata),
      balanceDate: undefined,
      balance: item.coverAmount ?? null,
    })),
  };
}

/**
 * Joins the members' rows in the order given. An item several members own comes back in each
 * one's list, so only the first member's row is kept: the item is listed and totalled once, and
 * opens through the earliest member who owns it.
 */
export function mergeGroupedRows(groups: GroupedRows[]): GroupedRows {
  const firstOfEachItem = (rows: FinancialRow[]) => {
    const seen = new Set<string>();
    return rows.filter(row => {
      if (seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    });
  };
  return {
    spendingAccounts: firstOfEachItem(groups.flatMap(group => group.spendingAccounts)),
    assets: firstOfEachItem(groups.flatMap(group => group.assets)),
    liabilities: firstOfEachItem(groups.flatMap(group => group.liabilities)),
    protections: firstOfEachItem(groups.flatMap(group => group.protections)),
  };
}

function buildSubSections(
  category: 'ASSET' | 'LIABILITY',
  rows: FinancialRow[],
): FinancialSubSection[] {
  const categoryInfo: CategoryInfo = financialAssetOptions[category];
  const knownProducts = new Set<string>(categoryInfo.productsOrder);
  const sections = categoryInfo.productsOrder
    .map((product): FinancialSubSection => {
      const productRows = rows.filter(row => row.product === product);
      return {
        key: product,
        title: categoryInfo.products[product].label,
        rows: productRows,
        ...totalsFor(productRows),
        showPolicyNumber: POLICY_NUMBER_PRODUCTS.has(product),
      };
    })
    .filter(section => section.rows.length > 0);

  // Capture any rows whose product isn't in productsOrder so the displayed
  // sub-sections always reconcile with the section total.
  const otherRows = rows.filter(row => !knownProducts.has(row.product));
  if (otherRows.length > 0) {
    sections.push({ key: 'OTHER', title: 'Other', rows: otherRows, ...totalsFor(otherRows) });
  }

  return sections;
}

/**
 * What the client is worth once their liabilities are taken off. Protection cover is left out:
 * it pays on an event rather than being value held today. Every total is already the client's
 * share, so a joint item moves net worth by what they own of it, not by its whole value.
 */
export function netWorthOf(categories: FinancialCategory[]): number {
  const totalFor = (key: FinancialCategoryKey) =>
    categories.find(category => category.key === key)?.total ?? 0;
  return totalFor('spendingAccounts') + totalFor('assets') - totalFor('liabilities');
}

export function buildCategories(grouped: GroupedRows): FinancialCategory[] {
  return [
    {
      key: 'spendingAccounts',
      title: 'Spending accounts',
      rows: grouped.spendingAccounts,
      ...totalsFor(grouped.spendingAccounts),
    },
    {
      key: 'assets',
      title: 'Assets',
      rows: grouped.assets,
      ...totalsFor(grouped.assets),
      subSections: buildSubSections('ASSET', grouped.assets),
    },
    {
      key: 'liabilities',
      title: 'Liabilities',
      rows: grouped.liabilities,
      ...totalsFor(grouped.liabilities),
      subSections: buildSubSections('LIABILITY', grouped.liabilities),
    },
    {
      key: 'protections',
      title: 'Protection policies',
      rows: grouped.protections,
      ...totalsFor(grouped.protections),
      totalLabel: 'Total cover',
    },
  ];
}
